import {useCallback, useEffect, useState} from 'react';

import type {EmblaCarouselType} from 'embla-carousel';

/**
 * Custom hook that provides comprehensive control logic for slider/carousel components using Embla Carousel.
 * This hook manages the state and behavior for navigation buttons, dot indicators, and scroll position tracking.
 * It handles initialization, selection updates, and provides callback functions for user interactions with the slider controls.
 *
 * @param embla The Embla Carousel instance to control. Can be undefined during initial render.
 * @returns An object containing all slider control properties and callback functions.
 *
 * @example
 * ```tsx
 * const emblaApi = useEmblaCarousel();
 * const {
 *     nextBtnDisabled,
 *     prevBtnDisabled,
 *     onNextButtonClick,
 *     onPrevButtonClick,
 *     onDotButtonClick,
 *     selectedIndex,
 *     scrollSnaps
 * } = useSliderControls(emblaApi);
 *
 * return (
 *     <div>
 *         <button disabled={prevBtnDisabled} onClick={onPrevButtonClick}>
 *             Previous
 *         </button>
 *         {scrollSnaps.map((_, index) => (
 *             <button
 *                 key={index}
 *                 onClick={() => onDotButtonClick(index)}
 *                 className={index === selectedIndex ? 'active' : ''}
 *             >
 *                 {index + 1}
 *             </button>
 *         ))}
 *         <button disabled={nextBtnDisabled} onClick={onNextButtonClick}>
 *             Next
 *         </button>
 *     </div>
 * );
 * ```
 */
export const useSliderControls = (embla?: EmblaCarouselType) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
    const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
    const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

    /**
     * Callback function to handle clicking the previous button and scroll to the previous slide.
     * This function safely checks for embla instance existence before attempting to scroll.
     * It uses useCallback to prevent unnecessary re-renders and maintain referential equality.
     *
     * @example
     * ```tsx
     * <button onClick={onPrevButtonClick} disabled={prevBtnDisabled}>
     *     Previous
     * </button>
     * ```
     */
    const onPrevButtonClick = useCallback(() => {
        if (!embla) return;
        embla.scrollPrev();
    }, [embla]);

    /**
     * Callback function to handle clicking the next button and scroll to the next slide.
     * This function safely checks for embla instance existence before attempting to scroll.
     * It uses useCallback to prevent unnecessary re-renders and maintain referential equality.
     *
     * @example
     * ```tsx
     * <button onClick={onNextButtonClick} disabled={nextBtnDisabled}>
     *     Next
     * </button>
     * ```
     */
    const onNextButtonClick = useCallback(() => {
        if (!embla) return;
        embla.scrollNext();
    }, [embla]);

    /**
     * Callback function to handle clicking a dot indicator and scroll to the specified slide index.
     * This function safely checks for embla instance existence before attempting to scroll to the target slide.
     * It uses useCallback to prevent unnecessary re-renders and maintain referential equality.
     *
     * @param index The zero-based index of the slide to scroll to.
     *
     * @example
     * ```tsx
     * {scrollSnaps.map((_, index) => (
     *     <button key={index} onClick={() => onDotButtonClick(index)}>
     *         {index + 1}
     *     </button>
     * ))}
     * ```
     */
    const onDotButtonClick = useCallback((index: number) => {
        if (!embla) return;
        embla.scrollTo(index);
    }, [embla]);

    /**
     * Callback function to handle carousel initialization and set up the scroll snap positions.
     * This function is called when the embla carousel is first initialized or re-initialized.
     * It retrieves and stores the available scroll snap list for dot indicator rendering.
     *
     * @param emblaApi The Embla Carousel API instance that has been initialized.
     *
     * @example
     * ```tsx
     * useEffect(() => {
     *     if (embla) {
     *         embla.on('reInit', onInit);
     *     }
     * }, [embla, onInit]);
     * ```
     */
    const onInit = useCallback((emblaApi: EmblaCarouselType) => {
        setScrollSnaps(emblaApi.scrollSnapList());
    }, []);

    /**
     * Callback function to handle carousel selection changes and update button states.
     * This function is triggered whenever the carousel selection changes, updating the disabled states of navigation buttons.
     * It also updates the currently selected index to reflect the active slide position.
     *
     * @param emblaApi The Embla Carousel API instance with updated selection state.
     *
     * @example
     * ```tsx
     * useEffect(() => {
     *     if (embla) {
     *         embla.on('select', onSelect);
     *     }
     * }, [embla, onSelect]);
     * ```
     */
    const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
        setPrevBtnDisabled(!emblaApi.canScrollPrev());
        setNextBtnDisabled(!emblaApi.canScrollNext());
        setSelectedIndex(emblaApi.selectedScrollSnap());
    }, []);

    useEffect(() => {
        if (!embla) return;

        onSelect(embla);
        onInit(embla);
        embla.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect);
    }, [embla, onInit, onSelect]);

    return {
        nextBtnDisabled,
        onDotButtonClick,
        onNextButtonClick,
        onPrevButtonClick,
        prevBtnDisabled,
        scrollSnaps,
        selectedIndex
    };
};